import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "form",
    "confirmModal",
    "confirmText",
    "confirmPaymentMethod",
    "confirmBillingAmount",
    "confirmSubmitButton",
    "confirmParticipantCount",
    "confirmParticipants",
    "confirmParticipantsEmpty",
    "robotSection",
    "savedCardSection",
    "newCardSection",
    "robotToken",
    "robotError"
  ]
  static values = {
    defaultPaymentLabel: String,
    feePerPersonYen: Number,
    robotAid: String,
    robotEmail: String,
    robotExistingCustomerUid: String,
    robotCheckoutAvailable: Boolean
  }

  connect() {
    this.handleEscape = this.handleEscape.bind(this)
    this.handleBeforeCache = this.handleBeforeCache.bind(this)

    document.addEventListener("keydown", this.handleEscape)
    document.addEventListener("turbo:before-cache", this.handleBeforeCache)
    this.syncPaymentUI()
  }

  disconnect() {
    document.removeEventListener("keydown", this.handleEscape)
    document.removeEventListener("turbo:before-cache", this.handleBeforeCache)
    this.resetBodyOverflow()
  }

  paymentMethodChanged() {
    this.syncPaymentUI()
    this.resetRobotToken()
  }

  cardSourceChanged() {
    this.syncPaymentUI()
    this.resetRobotToken()
  }

  openConfirm(event) {
    event.preventDefault()
    if (!this.hasConfirmModalTarget || !this.hasFormTarget) return

    const submitText = event.params.submitText || "この内容で申込む"

    if (this.hasConfirmSubmitButtonTarget) {
      this.confirmSubmitButtonTarget.textContent = submitText
    }

    if (this.hasConfirmTextTarget) {
      this.confirmTextTarget.textContent =
        submitText.includes("更新")
          ? "この内容で申込情報を更新します。よろしいですか？"
          : submitText.includes("決済")
            ? "この内容でカード決済を実行します。よろしいですか？"
            : "この内容で申込みます。よろしいですか？"
    }

    if (this.hasConfirmPaymentMethodTarget) {
      this.confirmPaymentMethodTarget.textContent = this.currentPaymentMethodLabel()
    }

    this.updateConfirmParticipants()
    this.openModal()
  }

  closeConfirm() {
    this.closeModal()
  }

  closeOnBackdrop(event) {
    if (!this.hasConfirmModalTarget) return
    if (event.target !== this.confirmModalTarget) return

    this.closeModal()
  }

  async submitConfirmed() {
    if (!this.hasFormTarget) return

    this.closeModal()

    const shouldContinue = await this.prepareRobotTokenIfNeeded()
    if (!shouldContinue) return

    this.formTarget.requestSubmit()
  }

  handleEscape(event) {
    if (event.key !== "Escape") return
    if (!this.hasConfirmModalTarget) return
    if (this.confirmModalTarget.classList.contains("hidden")) return

    this.closeModal()
  }

  handleBeforeCache() {
    this.closeModal()
  }

  openModal() {
    if (!this.hasConfirmModalTarget) return

    this.confirmModalTarget.classList.remove("hidden")
    this.confirmModalTarget.classList.add("flex")
    document.body.style.overflow = "hidden"
  }

  closeModal() {
    if (!this.hasConfirmModalTarget) return

    this.confirmModalTarget.classList.add("hidden")
    this.confirmModalTarget.classList.remove("flex")
    this.resetBodyOverflow()
  }

  resetBodyOverflow() {
    if (!this.hasConfirmModalTarget) return
    if (!this.confirmModalTarget.classList.contains("hidden")) return

    document.body.style.overflow = ""
  }

  syncPaymentUI() {
    const cardSelected = this.currentPaymentMethodIsCard()

    if (this.hasRobotSectionTarget) {
      this.robotSectionTarget.classList.toggle("hidden", !cardSelected)
    }

    const cardSource = this.currentCardSource()
    if (this.hasSavedCardSectionTarget) {
      this.savedCardSectionTarget.classList.toggle("hidden", cardSource !== "saved")
    }
    if (this.hasNewCardSectionTarget) {
      this.newCardSectionTarget.classList.toggle("hidden", cardSource === "saved")
    }

    if (!cardSelected || cardSource === "saved") {
      this.clearRobotError()
    }
  }

  updateConfirmParticipants() {
    const participants = this.collectParticipants()

    if (this.hasConfirmParticipantCountTarget) {
      this.confirmParticipantCountTarget.textContent = `${participants.length}名`
    }

    if (this.hasConfirmParticipantsTarget) {
      this.confirmParticipantsTarget.innerHTML = ""

      participants.forEach((participant) => {
        const li = document.createElement("li")
        li.className = "flex items-center justify-between gap-2 rounded-lg bg-slate-50 px-2.5 py-1.5 text-sm text-slate-700"

        const name = document.createElement("span")
        name.textContent = participant.name
        li.appendChild(name)

        if (participant.archived) {
          const badge = document.createElement("span")
          badge.className = "rounded-full bg-slate-200 px-2 py-0.5 text-[10px] font-semibold text-slate-700"
          badge.textContent = "削除済み"
          li.appendChild(badge)
        }

        this.confirmParticipantsTarget.appendChild(li)
      })
    }

    if (this.hasConfirmParticipantsEmptyTarget) {
      this.confirmParticipantsEmptyTarget.classList.toggle("hidden", participants.length > 0)
    }

    if (this.hasConfirmBillingAmountTarget) {
      this.confirmBillingAmountTarget.textContent = this.billingAmountLabel(participants.length)
    }
  }

  collectParticipants() {
    if (!this.hasFormTarget) return []

    const participants = []

    const existingRows = this.formTarget.querySelectorAll("[data-existing-contact-row]")
    existingRows.forEach((row) => {
      const participate = row.querySelector("[data-existing-contact-participation]")
      if (!participate || !participate.checked) return

      const lastName = row.querySelector("input[name*='[last_name]']")?.value?.trim() || ""
      const firstName = row.querySelector("input[name*='[first_name]']")?.value?.trim() || ""
      participants.push({ name: this.buildParticipantName(lastName, firstName), archived: false })
    })

    const archivedInputs = this.formTarget.querySelectorAll("input[name='event_application[contact_profile_ids][]'][data-archived-contact-name]")
    archivedInputs.forEach((input) => {
      const fullName = (input.dataset.archivedContactName || "").trim()
      participants.push({ name: fullName || "削除済み参加者", archived: true })
    })

    const newRows = this.formTarget.querySelectorAll("[data-new-contact-row]")
    newRows.forEach((row) => {
      const participate = row.querySelector("input[name='event_application[new_contact_profiles][][participate]']")
      if (!participate || !participate.checked) return

      const lastName = row.querySelector("input[name='event_application[new_contact_profiles][][last_name]']")?.value?.trim() || ""
      const firstName = row.querySelector("input[name='event_application[new_contact_profiles][][first_name]']")?.value?.trim() || ""
      participants.push({ name: this.buildParticipantName(lastName, firstName), archived: false })
    })

    return participants
  }

  buildParticipantName(lastName, firstName) {
    const fullName = [lastName, firstName].filter((value) => value).join(" ").trim()
    return fullName || "氏名未入力"
  }

  billingAmountLabel(participantCount) {
    const total = this.billingAmountYen(participantCount)
    if (total <= 0) return "無料"

    return `¥${new Intl.NumberFormat("ja-JP").format(total)}`
  }

  billingAmountYen(participantCount) {
    const feePerPersonYen = this.hasFeePerPersonYenValue ? Number(this.feePerPersonYenValue) : 0
    if (!Number.isFinite(feePerPersonYen) || feePerPersonYen <= 0) {
      return 0
    }

    return participantCount * feePerPersonYen
  }

  currentPaymentMethodLabel() {
    if (!this.hasFormTarget) {
      return this.defaultPaymentLabelValue || "クレジットカード"
    }

    const checked = this.formTarget.querySelector("input[name='event_application[payment_method]']:checked")
    if (!checked) {
      return this.defaultPaymentLabelValue || "クレジットカード"
    }

    return checked.dataset.paymentMethodLabel || (checked.value === "card" ? "クレジットカード" : "現金")
  }

  currentPaymentMethodIsCard() {
    if (!this.hasFormTarget) return false

    const checked = this.formTarget.querySelector("input[name='event_application[payment_method]']:checked")
    if (!checked) return false

    return checked.value === "card"
  }

  currentCardSource() {
    if (!this.hasFormTarget) return "new"

    const checked = this.formTarget.querySelector("input[name='event_application[robot_payment][card_source]']:checked")
    return checked ? checked.value : "new"
  }

  requiresRobotTokenization() {
    if (!this.currentPaymentMethodIsCard()) return false
    if (this.currentCardSource() === "saved") return false
    if (!this.hasRobotTokenTarget) return false
    if (this.billingAmountYen(this.collectParticipants().length) <= 0) return false

    return true
  }

  resetRobotToken() {
    if (!this.hasRobotTokenTarget) return

    this.robotTokenTarget.value = ""
  }

  async prepareRobotTokenIfNeeded() {
    this.clearRobotError()

    if (!this.requiresRobotTokenization()) {
      this.resetRobotToken()
      return true
    }

    if (!this.robotCheckoutAvailableValue || !this.robotAidValue) {
      this.showRobotError("現在カード決済を利用できません。決済方法を現金に変更してください。")
      return false
    }

    if (!window.CPToken || !window.ThreeDSAdapter) {
      this.showRobotError("決済モジュールの読み込みに失敗しました。ページを再読み込みしてください。")
      return false
    }
    if (!document.getElementById("EMV3DS_INPUT_FORM")) {
      this.showRobotError("3Dセキュア表示領域の初期化に失敗しました。ページを再読み込みしてください。")
      return false
    }

    const values = this.validateRobotCardInputs()
    if (!values) return false

    const participantCount = this.collectParticipants().length
    const amount = this.billingAmountYen(participantCount)
    if (amount <= 0) return true

    const originalText = this.hasConfirmSubmitButtonTarget ? this.confirmSubmitButtonTarget.textContent : ""
    if (this.hasConfirmSubmitButtonTarget) {
      this.confirmSubmitButtonTarget.disabled = true
      this.confirmSubmitButtonTarget.textContent = "認証中..."
    }

    try {
      await this.createRobotTokenAndAuthenticate(values, amount)
      this.clearRobotCardInputs()
      return true
    } catch (errorMessage) {
      this.showRobotError(errorMessage)
      return false
    } finally {
      if (this.hasConfirmSubmitButtonTarget) {
        this.confirmSubmitButtonTarget.disabled = false
        this.confirmSubmitButtonTarget.textContent = originalText
      }
    }
  }

  validateRobotCardInputs() {
    const cardNumberInput = document.getElementById("robot_event_cn")
    const expiryInput = document.getElementById("robot_event_ed")
    const cvcInput = document.getElementById("robot_event_cvv")
    const cardholderNameInput = document.getElementById("robot_event_name")

    if (!cardNumberInput || !expiryInput || !cvcInput || !cardholderNameInput) {
      this.showRobotError("カード入力欄の初期化に失敗しました。ページを再読み込みしてください。")
      return null
    }

    const cardNumber = this.normalizeDigits(cardNumberInput.value)
    const expiry = this.parseExpiry(expiryInput.value)
    const cvc = this.normalizeDigits(cvcInput.value)
    const cardholderName = this.parseCardholderName(cardholderNameInput.value)

    if (cardNumber.length < 12 || cardNumber.length > 19) {
      this.showRobotError("カード番号を確認してください。")
      return null
    }

    if (!expiry) {
      this.showRobotError("有効期限(MM/YY)を確認してください。")
      return null
    }

    if (cvc.length < 3 || cvc.length > 4) {
      this.showRobotError("セキュリティコード(CVC)を確認してください。")
      return null
    }

    if (!cardholderName) {
      this.showRobotError("カード名義(ローマ字)を入力してください。")
      return null
    }

    return {
      cardNumber: cardNumber,
      expiry: expiry.year + expiry.month,
      cvc: cvc,
      firstName: cardholderName.firstName,
      lastName: cardholderName.lastName
    }
  }

  createRobotTokenAndAuthenticate(values, amount) {
    return new Promise((resolve, reject) => {
      const callbackTimeoutMs = 20000
      let settled = false
      let tokenTimerId = null
      let authTimerId = null

      const clearTimers = () => {
        if (tokenTimerId) {
          window.clearTimeout(tokenTimerId)
          tokenTimerId = null
        }
        if (authTimerId) {
          window.clearTimeout(authTimerId)
          authTimerId = null
        }
      }
      const resolveOnce = (value) => {
        if (settled) return
        settled = true
        clearTimers()
        resolve(value)
      }
      const rejectOnce = (message) => {
        if (settled) return
        settled = true
        clearTimers()
        reject(message)
      }

      this.resetRobotToken()

      tokenTimerId = window.setTimeout(() => {
        rejectOnce("カード情報のトークン化がタイムアウトしました。時間を空けて再度お試しください。")
      }, callbackTimeoutMs)

      try {
        CPToken.TokenCreate({
          aid: this.robotAidValue,
          cn: values.cardNumber,
          ed: values.expiry,
          cvv: values.cvc,
          fn: values.firstName,
          ln: values.lastName
        }, (tokenResultCode, tokenErrMsg) => {
          if (tokenTimerId) {
            window.clearTimeout(tokenTimerId)
            tokenTimerId = null
          }
          if (tokenResultCode !== "Success") {
            rejectOnce(tokenErrMsg || `カード情報のトークン化に失敗しました。(${tokenResultCode})`)
            return
          }

          if (!this.hasRobotTokenTarget || !this.robotTokenTarget.value) {
            rejectOnce("決済トークンの取得に失敗しました。")
            return
          }

          const authParams = this.buildThreeDSAuthParams(amount)
          if (!authParams) {
            rejectOnce("3Dセキュア認証に必要な情報が不足しています。")
            return
          }

          authTimerId = window.setTimeout(() => {
            rejectOnce("3Dセキュア認証の応答がありません。時間を空けて再度お試しください。")
          }, callbackTimeoutMs)

          try {
            ThreeDSAdapter.authenticate(authParams, (authResultCode, authErrMsg) => {
              if (authTimerId) {
                window.clearTimeout(authTimerId)
                authTimerId = null
              }
              if (authResultCode !== "Success") {
                rejectOnce(authErrMsg || `3Dセキュア認証に失敗しました。(${authResultCode})`)
                return
              }

              resolveOnce(true)
            })
          } catch (error) {
            rejectOnce("3Dセキュア認証の開始に失敗しました。ページを再読み込みしてください。")
          }
        })
      } catch (error) {
        rejectOnce("カード情報のトークン化開始に失敗しました。ページを再読み込みしてください。")
      }
    })
  }

  buildThreeDSAuthParams(amount) {
    if (!this.hasRobotTokenTarget) return null

    const tokenValue = this.robotTokenTarget.value
    if (!tokenValue) return null

    if (this.shouldAuthenticateForCardRegistration()) {
      return {
        aid: this.robotAidValue,
        tkn: tokenValue,
        uid: this.robotExistingCustomerUidValue
      }
    }

    const email = (this.robotEmailValue || "").trim()
    if (!email) return null

    return {
      aid: this.robotAidValue,
      tkn: tokenValue,
      am: amount,
      tx: 0,
      sf: 0,
      em: email
    }
  }

  shouldAuthenticateForCardRegistration() {
    if (this.currentCardSource() !== "new") return false
    if (!this.hasRobotExistingCustomerUidValue || !this.robotExistingCustomerUidValue) return false

    const saveCheckbox = this.formTarget.querySelector("input[name='event_application[robot_payment][save_card]']")
    return !!saveCheckbox && saveCheckbox.checked
  }

  clearRobotCardInputs() {
    const ids = ["robot_event_cn", "robot_event_ed", "robot_event_cvv", "robot_event_name"]
    ids.forEach((id) => {
      const input = document.getElementById(id)
      if (input) input.value = ""
    })
  }

  normalizeDigits(value) {
    return (value || "").replace(/\D/g, "")
  }

  parseExpiry(value) {
    const digits = this.normalizeDigits(value)
    let month = ""
    let year = ""

    if (digits.length === 4) {
      const firstPair = digits.slice(0, 2)
      const secondPair = digits.slice(2, 4)
      const firstAsMonth = Number.parseInt(firstPair, 10)
      const secondAsMonth = Number.parseInt(secondPair, 10)

      if (firstAsMonth >= 1 && firstAsMonth <= 12) {
        month = firstPair
        year = secondPair
      } else if (secondAsMonth >= 1 && secondAsMonth <= 12) {
        year = firstPair
        month = secondPair
      }
    } else if (digits.length === 6) {
      const mm = digits.slice(0, 2)
      const yyyy = digits.slice(2, 6)
      const mmNumber = Number.parseInt(mm, 10)

      if (mmNumber >= 1 && mmNumber <= 12) {
        month = mm
        year = yyyy.slice(2, 4)
      }
    }

    if (!month || !year) return null
    return { month: month, year: year }
  }

  parseCardholderName(value) {
    const normalized = (value || "").trim().replace(/\s+/g, " ")
    if (!normalized) return null

    const parts = normalized.split(" ")
    if (parts.length === 1) {
      return { firstName: normalized, lastName: normalized }
    }

    return {
      firstName: parts[0],
      lastName: parts.slice(1).join(" ")
    }
  }

  showRobotError(message) {
    if (!this.hasRobotErrorTarget) return

    this.robotErrorTarget.textContent = message || "カード決済に失敗しました。"
    this.robotErrorTarget.classList.remove("hidden")
    this.robotErrorTarget.scrollIntoView({ behavior: "smooth", block: "center" })
  }

  clearRobotError() {
    if (!this.hasRobotErrorTarget) return

    this.robotErrorTarget.textContent = ""
    this.robotErrorTarget.classList.add("hidden")
  }
}
